import React, { useState } from 'react';
import {
  Button,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import AttachmentIcon from '@mui/icons-material/Attachment';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CampaignIcon from '@mui/icons-material/Campaign';

/**
 * Ações da listagem: botão primário + Ver + menu ⋯ (secundárias).
 */
export default function SolicitacaoRowActions({
  item,
  podeGerirTR,
  temTRDisponivel,
  enviandoProcesso,
  onVer,
  onExcluir,
  onAbrirCI,
  onAbrirTR,
  onEnviarProcesso,
  onAbrirProcesso,
  onAbrirAditivo,
  onAbrirNotificacao,
  onBaixarTrPdf,
  onBaixarTrDocx,
  onAbrirAnexo,
}) {
  const [anchor, setAnchor] = useState(null);
  const isCI = item.tipo === 'CI';
  const isCIContratacao = item.tipo === 'CI' && item.ciTipo === 'CONTRATACAO';
  const isCIAditivo = item.tipo === 'CI' && item.ciTipo === 'ADITIVO';
  const isNotificacao = item.tipo === 'NOTIFICACAO';
  const temProcesso = !!item.processo?.id;

  const primaria = (() => {
    if (isCIContratacao) {
      if (!temTRDisponivel) {
        return {
          label: 'Preencher TR',
          color: 'warning',
          variant: 'contained',
          icon: <EditIcon fontSize="small" />,
          onClick: () => onAbrirTR(item),
        };
      }
      return {
        label: temProcesso ? 'Abrir Processo' : 'Abrir processo',
        color: 'primary',
        variant: 'contained',
        icon: <SendIcon fontSize="small" />,
        onClick: () => (temProcesso ? onAbrirProcesso() : onEnviarProcesso(item)),
        disabled: enviandoProcesso,
      };
    }
    if (isCIAditivo) {
      return {
        label: temProcesso ? 'Abrir Processo' : 'Iniciar Aditivo',
        color: 'primary',
        variant: 'contained',
        icon: temProcesso ? <SendIcon fontSize="small" /> : <EditIcon fontSize="small" />,
        onClick: () => (temProcesso ? onAbrirProcesso() : onAbrirAditivo(item)),
      };
    }
    if (isNotificacao) {
      return {
        label: 'Gerar Notificação',
        color: 'primary',
        variant: 'contained',
        icon: <CampaignIcon fontSize="small" />,
        onClick: () => onAbrirNotificacao(item),
      };
    }
    return null;
  })();

  const fechar = () => setAnchor(null);

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
      {isCIContratacao && !temTRDisponivel && (
        <Chip size="small" color="warning" label="TR pendente (fiscal)" sx={{ fontWeight: 700, height: 22 }} />
      )}
      {primaria && (
        <Button
          size="small"
          variant={primaria.variant}
          color={primaria.color}
          startIcon={primaria.icon}
          disabled={primaria.disabled}
          onClick={primaria.onClick}
          sx={{ textTransform: 'none', borderRadius: 2, whiteSpace: 'nowrap' }}
        >
          {primaria.label}
        </Button>
      )}
      <Tooltip title="Ver detalhes">
        <IconButton size="small" onClick={() => onVer(item)} color="primary">
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Mais ações">
        <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={fechar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {isCI && onAbrirCI && (
          <MenuItem
            onClick={() => {
              fechar();
              onAbrirCI(item);
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Editar CI no sistema</ListItemText>
          </MenuItem>
        )}
        {isCIContratacao && (
          <MenuItem
            onClick={() => {
              fechar();
              onAbrirTR(item);
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{temTRDisponivel ? 'Revisar TR' : 'Preencher TR'}</ListItemText>
          </MenuItem>
        )}
        {isCIContratacao && item.trPdfPath && (
          <MenuItem
            onClick={() => {
              fechar();
              onBaixarTrPdf(item);
            }}
          >
            <ListItemIcon>
              <PictureAsPdfIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Baixar TR (PDF)</ListItemText>
          </MenuItem>
        )}
        {isCIContratacao && item.trDocxPath && (
          <MenuItem
            onClick={() => {
              fechar();
              onBaixarTrDocx(item);
            }}
          >
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Baixar TR (Word)</ListItemText>
          </MenuItem>
        )}
        {isCIContratacao && temTRDisponivel && (
          <MenuItem
            disabled={enviandoProcesso}
            onClick={() => {
              fechar();
              temProcesso ? onAbrirProcesso() : onEnviarProcesso(item);
            }}
          >
            <ListItemIcon>
              <SendIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{temProcesso ? 'Abrir Processo' : 'Abrir processo de contratação'}</ListItemText>
          </MenuItem>
        )}
        {isCIAditivo && (
          <MenuItem
            onClick={() => {
              fechar();
              onAbrirAditivo(item);
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Iniciar / editar Aditivo</ListItemText>
          </MenuItem>
        )}
        {isCIAditivo && (
          <MenuItem
            disabled={enviandoProcesso}
            onClick={() => {
              fechar();
              temProcesso ? onAbrirProcesso() : onEnviarProcesso(item);
            }}
          >
            <ListItemIcon>
              <SendIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{temProcesso ? 'Abrir Processo' : 'Enviar para Análise'}</ListItemText>
          </MenuItem>
        )}
        {isNotificacao && (
          <MenuItem
            onClick={() => {
              fechar();
              onAbrirNotificacao(item);
            }}
          >
            <ListItemIcon>
              <CampaignIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Gerar Notificação</ListItemText>
          </MenuItem>
        )}
        {item.arquivoUrl && (
          <MenuItem
            onClick={() => {
              fechar();
              onAbrirAnexo(item);
            }}
          >
            <ListItemIcon>
              <AttachmentIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Abrir anexo</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            fechar();
            onExcluir(item);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Excluir</ListItemText>
        </MenuItem>
      </Menu>
    </Stack>
  );
}
